import { useState } from "react";

import { toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { postAgreement } from "~/v2/api/rest/agreements";
import type { SubscribeStepProps } from "../Subscribe.controller";
import { AgreementsPres } from "./Agreements.pres";

type AgreementState = {
  isOpen: boolean;
  isAccepted: boolean;
};

export type AgreementsState = {
  ctaAgreement: AgreementState;
  utpAgreement: AgreementState;
  opraAgreement: AgreementState;
};

const AGREEMENT_MAP: Record<
  keyof AgreementsState,
  {
    mutationID: string;
    amplitudeEventName: string;
  }
> = {
  ctaAgreement: {
    mutationID: "nyse",
    amplitudeEventName: "md_cta_clicked",
  },
  utpAgreement: {
    mutationID: "nasdaq",
    amplitudeEventName: "md_utp_clicked",
  },
  opraAgreement: {
    mutationID: "opra_agreement",
    amplitudeEventName: "md_opra_clicked",
  },
};

export const AgreementsConnector = ({
  account,
  details,
  isLegalEntity,
  legalEntityOwner,
  accountOpener,
  onBackToPreviousStep,
  onContinueToNextStep,
}: SubscribeStepProps) => {
  const accountId = account?.id ?? "";
  const [state, setState] = useState<AgreementsState>({
    ctaAgreement: {
      isOpen: false,
      isAccepted: false,
    },
    utpAgreement: {
      isOpen: false,
      isAccepted: false,
    },
    opraAgreement: {
      isOpen: false,
      isAccepted: false,
    },
  });

  const closeAgreement = (agreement: keyof AgreementsState) =>
    setState((prev) => ({
      ...prev,
      [agreement]: { ...prev[agreement], isOpen: false },
    }));

  const onOpenAgreement = (agreement: keyof AgreementsState) => {
    if (!accountId) {
      toastError("Unable to load agreement preview: missing account");
      return;
    }

    setState((prev) => ({
      ...prev,
      [agreement]: { ...prev[agreement], isOpen: true },
    }));
  };

  const onAgreeAgreement = (agreement: keyof AgreementsState) =>
    signAgreementMutation.mutate(agreement);

  const signAgreementMutation = useMutationRequest(
    (agreement: keyof AgreementsState) =>
      postAgreement({
        ownerId: isLegalEntity
          ? (legalEntityOwner?.owner_id ?? "")
          : (details?.owner_id ?? ""),
        agreement: AGREEMENT_MAP[agreement].mutationID,
        accountId,
        signerPartyId:
          isLegalEntity && accountOpener?.owner_id
            ? accountOpener.owner_id
            : undefined,
      }),
    {
      onSuccess: (_response, agreement) => {
        setState((prev) => ({
          ...prev,
          [agreement]: { ...prev[agreement], isAccepted: true, isOpen: false },
        }));
        AmplitudeProvider.dispatch(AGREEMENT_MAP[agreement].amplitudeEventName);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to sign agreement: ${errorMessage}`);
      },
    },
  );

  const onContinueClicked = () => {
    const isAllAgreementsAccepted = Object.values(state).every(
      (agreement) => agreement.isAccepted,
    );

    if (!isAllAgreementsAccepted) {
      toastError("Please accept all agreements to continue");
      return;
    }

    onContinueToNextStep();
  };

  const agreements = (Object.keys(state) as (keyof AgreementsState)[]).reduce(
    (acc, key) => {
      acc[key] = {
        ...state[key],
        agreementId: AGREEMENT_MAP[key].mutationID,
      };
      return acc;
    },
    {} as Record<
      keyof AgreementsState,
      { isOpen: boolean; isAccepted: boolean; agreementId: string }
    >,
  );

  const isContinueButtonDisabled = Object.values(state).some(
    (agreement) => !agreement.isAccepted,
  );

  const isAgreementMutating = signAgreementMutation.isPending;

  return (
    <AgreementsPres
      accountId={accountId}
      agreements={agreements}
      onOpenAgreement={onOpenAgreement}
      onCloseAgreement={closeAgreement}
      onAgreeAgreement={onAgreeAgreement}
      onBackClicked={onBackToPreviousStep}
      onContinueClicked={onContinueClicked}
      isContinueButtonDisabled={isContinueButtonDisabled}
      isAgreementMutating={isAgreementMutating}
    />
  );
};
